/* ==========================================================================
   Ozeret — the first five minutes

   Somebody who has just signed up is looking at a feed of strangers and a
   row of tabs. This walks them along the tabs once, in order, saying what
   each one is for in a sentence, and then gets out of the way for good.

   It is a card, not a tooltip system. Tooltips that point at things need to
   know where the things are, which on a phone with a keyboard and a
   rotating screen they do not. A card at the bottom, and the tab it is
   talking about lit up, is enough — and it shows the real screen behind it
   rather than a picture of one, so what they learn is where things are.

   Shown once per side, per device. Available again from Settings.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import { Icon } from './Icon';
import { commit, state, tab, type ScreenName } from '../store';

interface Step { view: ScreenName; title: string; text: string }

const STEPS: Record<'house' | 'cleaner', Step[]> = {
  house: [
    { view: 'h_find', title: 'tour_h1_t', text: 'tour_h1' },
    { view: 'h_post', title: 'tour_h2_t', text: 'tour_h2' },
    { view: 'h_requests', title: 'tour_h3_t', text: 'tour_h3' },
    { view: 'h_messages', title: 'tour_h4_t', text: 'tour_h4' },
    { view: 'h_profile', title: 'tour_h5_t', text: 'tour_h5' },
  ],
  cleaner: [
    { view: 'c_jobs', title: 'tour_c1_t', text: 'tour_c1' },
    { view: 'c_calendar', title: 'tour_c2_t', text: 'tour_c2' },
    { view: 'c_bookings', title: 'tour_c3_t', text: 'tour_c3' },
    { view: 'c_messages', title: 'tour_c4_t', text: 'tour_c4' },
    { view: 'c_profile', title: 'tour_c5_t', text: 'tour_c5' },
  ],
};

const KEY = (role: string) => 'ozeret.tour.' + role;

export function seenTour(role: string): boolean {
  try {
    return localStorage.getItem(KEY(role)) === '1';
  } catch {
    return false;
  }
}

function markSeen(role: string): void {
  try {
    localStorage.setItem(KEY(role), '1');
  } catch { /* nothing to do */ }
}

export function Tour() {
  const role = state.tour;
  const [i, setI] = useState(0);
  /* Where they were when the walk began -- Settings, if they asked for it
     again; a half-finished form, if it appeared on its own. */
  const [origin] = useState(() => ({
    view: state.view, params: state.params, stack: [...state.stack],
  }));
  const steps = role ? STEPS[role] : [];
  const step = steps[i];

  /* Each step shows its own screen behind the card, so the tab lights up and
     the thing being described is actually there. */
  useEffect(() => {
    if (step && state.view !== step.view) tab(step.view);
  }, [step]);

  if (!role || !step) return null;

  const done = () => {
    markSeen(role);
    state.tour = null;
    /* Back where they were. This used to send everybody to the first tab
       with their place cleared, so closing the walk from Settings -- or
       closing one that appeared over something half done -- felt like the
       page had reset. */
    state.view = origin.view;
    state.params = origin.params;
    state.stack = origin.stack;
    state.sheet = null;
    commit();
  };

  const last = i === steps.length - 1;

  return (
    <div className="tour" role="dialog" aria-label={t('tour_title')}>
      <div className="tour-card">
        <button type="button" className="tour-x" onClick={done} aria-label={t('tour_skip')}>
          <Icon name="x" />
        </button>
        <div className="tour-dots" aria-hidden="true">
          {steps.map((_, n) => <i key={n} className={n === i ? 'on' : ''} />)}
        </div>
        <b className="tour-head">{t(step.title)}</b>
        <span className="tour-text">{t(step.text)}</span>
        <div className="row" style={{ gap: 8, marginTop: 4 }}>
          <button type="button" className="btn quiet sm" onClick={done}>
            {t('tour_skip')}
          </button>
          <span className="grow" />
          {i > 0 && (
            <button type="button" className="btn ghost sm" onClick={() => setI(i - 1)}>
              {t('tour_back')}
            </button>
          )}
          <button
            type="button"
            className="btn sm"
            onClick={() => (last ? done() : setI(i + 1))}
          >
            {last ? t('tour_done') : t('tour_next')}
          </button>
        </div>
      </div>
    </div>
  );
}
